/* eslint max-lines-per-function: ["error", {max: 217}] -- many validations and handler functions inherent to the feature */
import { useRef, useState } from "react";

import {
  extractMentionQuery,
  shouldTriggerMention,
} from "@/utils/mentionTextInput.util";

interface MentionedUser {
  end: number;
  start: number;
  userId: string;
  userName: string;
}

const useMentionTextInput = (members?: BaseUserAvatarProps[]) => {
  const mentionedUsersRef = useRef<MentionedUser[]>([]);
  const prevRef = useRef<{
    selection: { end: number; start: number };
    text: string;
  }>({
    selection: { end: 0, start: 0 },
    text: "",
  });

  const [message, setMessage] = useState("");
  const [selection, setSelection] = useState({ end: 0, start: 0 });
  const [mentionQuery, setMentionQuery] = useState<null | string>(null);
  const [mentionCandidates, setMentionCandidates] =
    useState<BaseUserAvatarProps[]>();

  // Clears mention search state
  const resetMentionState = () => {
    setMentionQuery(null);
    setMentionCandidates(undefined);
  };

  // Processes mention query and updates state
  const processMentionQuery = ({
    cursor,
    text,
  }: {
    cursor: number;
    text: string;
  }) => {
    if (shouldTriggerMention({ cursor, text })) {
      triggerMention();
      return;
    }

    const query = extractMentionQuery({ cursor, text });
    if (!query) {
      resetMentionState();
      return;
    }

    updateMentionCandidates(query);
  };

  const triggerMention = () => {
    setMentionQuery("@");
    setMentionCandidates(members?.filter((user) => user.userId) ?? []);
  };

  const updateMentionCandidates = (query: string) => {
    setMentionQuery(query);
    setMentionCandidates(
      members?.filter((member) =>
        member.userName.toLowerCase().includes(query.toLowerCase()),
      ) ?? [],
    );
  };

  // Adjusts mention ranges after text changes
  const reconcileMentionRanges = ({
    basePosition,
    offset,
  }: {
    basePosition: number;
    offset: number;
  }) => {
    mentionedUsersRef.current = mentionedUsersRef.current.map(
      (mentionedUser) => {
        if (mentionedUser.start >= basePosition) {
          return {
            ...mentionedUser,
            end: mentionedUser.end + offset,
            start: mentionedUser.start + offset,
          };
        }
        return mentionedUser;
      },
    );
  };

  // Deletes full mention if backspacing within one
  const handleBackspace = (): boolean => {
    const cursor = selection.start;
    const mention = mentionedUsersRef.current.find(
      (user) => cursor <= user.end && cursor > user.start,
    );
    if (!mention) return false;

    const updatedText =
      message.slice(0, mention.start) +
      (message[mention.end] === " "
        ? message.slice(mention.end)
        : ` ${message.slice(mention.end)}`);
    const removedLength = mention.end - mention.start;

    mentionedUsersRef.current = mentionedUsersRef.current.filter(
      (user) => user.start !== mention.start,
    );

    reconcileMentionRanges({
      basePosition: mention.end,
      offset: -removedLength,
    });

    const newCursor = mention.start;
    setMessage(updatedText);
    setSelection({ end: newCursor, start: newCursor });
    processMentionQuery({ cursor: newCursor, text: updatedText });

    prevRef.current = {
      selection: { end: newCursor, start: newCursor },
      text: updatedText,
    };
    return true;
  };

  // Handles normal input changes, triggers mention if needed
  const handleInputChange = (newText: string) => {
    const { text: prevText } = prevRef.current;
    const cursor = selection.start;
    const offset = newText.length - prevText.length;

    if (offset < 0 && handleBackspace()) return;

    if (offset > 0) {
      reconcileMentionRanges({ basePosition: cursor - offset, offset });
    }

    setMessage(newText);
    processMentionQuery({ cursor, text: newText });

    prevRef.current = { selection: { ...selection }, text: newText };
  };

  // Handles mention selection and inserts mention text
  const handleMentionSelect = (user: BaseUserAvatarProps) => {
    const match = new RegExp(/@(\w*)$/).exec(message.slice(0, selection.start));
    const insertAt = match?.index ?? selection.start;

    const mentionText = `@${user.userName} `;
    const newText =
      message.slice(0, insertAt) + mentionText + message.slice(selection.start);

    const mentionStart = insertAt;
    const mentionEnd = mentionStart + mentionText.length;

    mentionedUsersRef.current.push({
      end: mentionEnd,
      start: mentionStart,
      userId: user.userId,
      userName: user.userName,
    });

    const newCursor = mentionEnd;
    setMessage(newText);
    setSelection({ end: newCursor, start: newCursor });
    resetMentionState();

    prevRef.current = {
      selection: { end: newCursor, start: newCursor },
      text: newText,
    };
  };

  // Blocks cursor from entering inside mention range
  const handleSelectionChange = (e: {
    nativeEvent: { selection: { end: number; start: number } };
  }) => {
    const next = e.nativeEvent.selection;

    if (next.start !== next.end) {
      setSelection(next);
      return;
    }

    const cursor = next.start;
    const overlappingMention = mentionedUsersRef.current.find(
      (mention) => cursor > mention.start && cursor < mention.end,
    );

    if (overlappingMention) {
      const adjusted = {
        end: overlappingMention.end,
        start: overlappingMention.end,
      };
      setSelection(adjusted);
    } else {
      setSelection(next);
    }
  };

  // Resets entire mention input state
  const resetState = () => {
    setMessage("");
    setSelection({ end: 0, start: 0 });
    mentionedUsersRef.current = [];
    resetMentionState();
  };

  // Returns unique mentioned userIds
  const getMentions = () =>
    Array.from(new Set(mentionedUsersRef.current.map((m) => m.userId)));

  return {
    getMentions,
    handleInputChange,
    handleMentionSelect,
    handleSelectionChange,
    mentionCandidates,
    mentionedUsers: mentionedUsersRef.current,
    mentionQuery,
    message,
    resetState,
    selection,
    setMessage,
  };
};

export { useMentionTextInput };
